import { ref, computed, watch } from 'vue'
import { configurationsService } from '@/services/api-services'

// Estado global do whitelabel
const whitelabelConfig = ref({
  appName: 'FlowPilot',
  logo: null as string | null,
  logo_auth: null as string | null,
  primaryColor: '#007bff',
  secondaryColor: '#6c757d',
  accentColor: '#28a745',
  globalNotificationsEnabled: true,
  defaultLanguage: 'pt-BR',
  defaultTimezone: 'America/Sao_Paulo',
  dateFormat: 'DD/MM/YYYY',
  timeFormat: 'HH:mm',
  darkModeEnabled: false
})

const isLoading = ref(false)
const isInitialized = ref(false)
const hasLoadedRemote = ref(false)

// Função para aplicar estilos dinamicamente
const applyWhitelabelStyles = () => {
  const root = document.documentElement
  const { primaryColor, secondaryColor, accentColor } = whitelabelConfig.value
  
  // Aplicar variáveis CSS principais
  root.style.setProperty('--primary-color', primaryColor)
  root.style.setProperty('--secondary-color', secondaryColor)
  root.style.setProperty('--accent-color', accentColor)
  
  // Aplicar variáveis do KT Metronic
  root.style.setProperty('--kt-primary', primaryColor)
  root.style.setProperty('--kt-primary-light', primaryColor)
  root.style.setProperty('--kt-primary-dark', primaryColor)
  root.style.setProperty('--kt-secondary', secondaryColor)
  root.style.setProperty('--kt-accent', accentColor)
  
  // Aplicar variações com transparência
  root.style.setProperty('--kt-primary-rgb', hexToRgb(primaryColor))
  root.style.setProperty('--kt-secondary-rgb', hexToRgb(secondaryColor))
  root.style.setProperty('--kt-accent-rgb', hexToRgb(accentColor))
  
  // Forçar re-render de elementos com cores dinâmicas
  const elementsWithDynamicColors = document.querySelectorAll('[style*="primary"], [style*="secondary"], [style*="accent"]')
  elementsWithDynamicColors.forEach(el => {
    const style = (el as HTMLElement).style.cssText
    if (style.includes('primary') || style.includes('secondary') || style.includes('accent')) {
      // Força o re-render removendo e readicionando o style
      ;(el as HTMLElement).style.cssText = ''
      setTimeout(() => {
        ;(el as HTMLElement).style.cssText = style
      }, 0)
    }
  })
  
  // Aplicar tema escuro/claro usando data-theme (padrão KT UI)
  if (whitelabelConfig.value.darkModeEnabled) {
    document.documentElement.setAttribute('data-theme', 'dark')
    document.documentElement.classList.add('dark') // Necessário para Tailwind CSS
    document.body.classList.add('dark-theme') // Manter para compatibilidade com CSS customizado
  } else {
    document.documentElement.removeAttribute('data-theme')
    document.documentElement.classList.remove('dark') // Remover classe dark do Tailwind
    document.body.classList.remove('dark-theme')
  }
  
  // Atualizar título da página
  document.title = whitelabelConfig.value.appName
}

// Função auxiliar para converter hex para RGB
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result 
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '0, 0, 0'
}

export function useWhitelabel() {
  // Aplicar estilos padrão na primeira chamada
  if (!isInitialized.value) {
    applyWhitelabelStyles()
    isInitialized.value = true
  }

  // Função para carregar configurações do whitelabel
  const loadWhitelabelConfig = async (force = false) => {
    try {
      if (isLoading.value) return
      if (hasLoadedRemote.value && !force) return

      isLoading.value = true
      
      // Primeiro, verificar se há configurações salvas no localStorage
      const savedDarkMode = localStorage.getItem('whitelabel-dark-mode')
      const ktTheme = localStorage.getItem('kt-theme')
      
      if (savedDarkMode !== null) {
        whitelabelConfig.value.darkModeEnabled = savedDarkMode === 'true'
      } else if (ktTheme) {
        whitelabelConfig.value.darkModeEnabled = ktTheme === 'dark'
      }
      
      const config = await configurationsService.getCurrent()
      
      // A API agora retorna um objeto diretamente com as propriedades
      if (config) {
        // Extrair apenas as propriedades relevantes para o whitelabel
        const whitelabelData = {
          appName: config.appName || whitelabelConfig.value.appName,
          logo: config.logo || null,
          logo_auth: config.logo_auth || null,
          primaryColor: config.primaryColor || whitelabelConfig.value.primaryColor,
          secondaryColor: config.secondaryColor || whitelabelConfig.value.secondaryColor,
          accentColor: config.accentColor || whitelabelConfig.value.accentColor,
          globalNotificationsEnabled: config.globalNotificationsEnabled ?? whitelabelConfig.value.globalNotificationsEnabled,
          defaultLanguage: config.defaultLanguage || whitelabelConfig.value.defaultLanguage,
          defaultTimezone: config.defaultTimezone || whitelabelConfig.value.defaultTimezone,
          dateFormat: config.dateFormat || whitelabelConfig.value.dateFormat,
          timeFormat: config.timeFormat || whitelabelConfig.value.timeFormat,
          darkModeEnabled: config.darkModeEnabled ?? whitelabelConfig.value.darkModeEnabled
        }
        
        whitelabelConfig.value = { ...whitelabelConfig.value, ...whitelabelData }
      } else {
        console.log('📝 Nenhuma configuração encontrada na API, usando padrão')
      }
      
      // Sempre aplicar os estilos após carregar
      applyWhitelabelStyles()
      hasLoadedRemote.value = true
      
    } catch (error: any) {
      // Não lançar erro se for problema de autenticação
      if (error.status === 401 || error.status === 403) {
        console.log('📝 Usuário não autenticado, usando configuração padrão de whitelabel')
      } else {
        console.warn('⚠️ Erro ao carregar whitelabel, usando configuração padrão:', error)
      }
      // Aplicar tema mesmo com erro
      applyWhitelabelStyles()
    } finally {
      isLoading.value = false
    }
  }

  // Função para atualizar configurações do whitelabel
  const updateWhitelabelConfig = async (updates: Partial<typeof whitelabelConfig.value>) => {
    try {
      isLoading.value = true
      
      // Atualizar localmente primeiro para mudança imediata
      whitelabelConfig.value = { ...whitelabelConfig.value, ...updates }
      
      // Aplicar mudanças visuais imediatamente
      applyWhitelabelStyles()
      
      // Também salvar no localStorage para persistência
      if (updates.darkModeEnabled !== undefined) {
        localStorage.setItem('kt-theme', updates.darkModeEnabled ? 'dark' : 'light')
        localStorage.setItem('whitelabel-dark-mode', updates.darkModeEnabled.toString())
      }
      
      // Enviar para a API (em background)
      await configurationsService.updateCurrent(updates)
      
    } catch (error) {
      console.error('❌ Erro ao atualizar whitelabel:', error)
      // Em caso de erro, reverter as mudanças locais
      // (isso é opcional, dependendo da UX desejada)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  // Computed para facilitar o acesso
  const appName = computed(() => whitelabelConfig.value.appName)
  const logo = computed(() => whitelabelConfig.value.logo)
  const primaryColor = computed(() => whitelabelConfig.value.primaryColor)
  const secondaryColor = computed(() => whitelabelConfig.value.secondaryColor)
  const accentColor = computed(() => whitelabelConfig.value.accentColor)
  const isDarkMode = computed(() => whitelabelConfig.value.darkModeEnabled)
  const globalNotifications = computed(() => whitelabelConfig.value.globalNotificationsEnabled)
  
  // Função para obter URL da logo
  const getLogoUrl = (filename: string | null) => {
    if (!filename) return null
    const url = configurationsService.getLogoUrl(filename)
    return url
  }

  // Watcher para aplicar estilos quando as configurações mudarem
  watch(whitelabelConfig, applyWhitelabelStyles, { deep: true })

  return {
    // Estado
    whitelabelConfig: computed(() => whitelabelConfig.value),
    isLoading: computed(() => isLoading.value),
    
    // Propriedades computadas
    appName,
    logo,
    primaryColor,
    secondaryColor,
    accentColor,
    isDarkMode,
    globalNotifications,
    
    // Métodos
    loadWhitelabelConfig,
    updateWhitelabelConfig,
    applyWhitelabelStyles,
    getLogoUrl
  }
}